// branch-browser.ts — the branch-browse modal (seam branch-tree-and-sha-
// timeline; R51, R52 display owner, R52a).
//
// Renders the FROZEN /api/vcs/branches contract (corpus/browse.ts — this
// module is forbidden from reshaping it) as an expandable/collapsable tree
// in the ADO shape: the tree is built from BRANCH-NAME '/' segments
// (docs/, elite/), NOT a repo path listing, and it is HAND-ROLLED —
// ag-grid's Tree Data is enterprise/unlicensed here. The expansion model is
// the corpus-shell.ts PATTERN (an opted-open Set + a structure-signature
// guard so re-renders only rebuild when the visible structure changed) —
// pattern only, never an import: corpus-shell.ts lives in the Helm bundle.
//
// R52 — THIS SEAM'S ACCEPTANCE IS THE DISPLAY: the seven ADO columns
// (Branch, Commit, Author, Authored Date, Behind | Ahead, Status,
// Pull Request) render EXACTLY as /api/vcs serves them. The five
// git-answerable columns carry the wire's real values; Status and
// Pull Request are a NAMED per-column unavailability — the cell reads
// 'not available' and its title carries the server's reason sentence
// VERBATIM (columns.status.reason / columns.pullRequest.reason). Never
// blank, never fabricated: every cell VM below is measured by
// branch-browse.test.mjs per column, including each unavailability form.
//
// R52a — the per-row star (favourite/track; glyph `star`, shipped by
// icon-roster — this seam must NOT touch SPRITE_ICONS) and the overflow (…)
// menu (glyph `ellipsis`) riding ctxmenu.ts, the console's existing menu
// singleton — never a third menu engine. Star persistence is the ui_state
// kind 'branch_fav' (migration 064; this seam is the ONLY writer of
// ui-state.ts's KINDS allowlist), one seat per user at
// /api/ui-state/branch-favourites/code.
//
// Refusal posture (openForkPicker's, ported): a failed listing renders the
// refusal VERBATIM ("Branch listing unavailable (502): …") — a mirror
// failure must never read as "no branches". The measured header line
// (payload.measured) and any serve-stale sentence (freshness.stale) render
// verbatim too.
//
// DOM discipline: createElement/textContent/classList throughout (the
// dialog.ts precedent) so the stub-document harness in
// branch-browse.test.mjs can drive the REAL modal without a browser; the
// only innerHTML writes are icon() sprite references.

import { openDialog, showDialogMsg, stackHostFor, type DialogHandle } from './dialog.js';
import { openCtxMenu, copyPlainText, type CtxMenuItem } from './ctxmenu.js';
import { icon } from './generated/icons.js';
import { mkIconBtn, setBtnLabel, setBtnState } from './buttons.js';
import { openShaTimeline, type CorpusAnswer } from './sha-timeline.js';
import { attachElementScrollRail } from 'pi-stack/client/scroll-rails.js';

// ---------------------------------------------------------------------
// The frozen wire (corpus/browse.ts) — mirrored, never reshaped.
// ---------------------------------------------------------------------

export interface VcsBranchRow {
  name: string;
  sha: string;
  author: string;
  authoredDate: string;
  subject: string;
  ahead: number | null;
  behind: number | null;
  status: unknown | null;
  pullRequest: unknown | null;
}

export interface VcsColumnGap { available: boolean; reason: string | null }

export interface VcsBranchesPayload {
  base: string | null;
  branches: VcsBranchRow[];
  total: number;
  matched: number;
  truncated: boolean;
  /** Owner item 14 (D8): the server pages the in-memory slice; the client
   * fetches until nextOffset is null so EVERY branch flows through. */
  offset?: number;
  nextOffset?: number | null;
  columns: { status: VcsColumnGap; pullRequest: VcsColumnGap; aheadBehind: VcsColumnGap };
  freshness: { stale: string | null };
  measured: string;
}

/** The seven ADO columns — a FLOOR, in ADO's own order. */
export const BRANCH_COLUMNS = [
  'Branch', 'Commit', 'Author', 'Authored Date', 'Behind | Ahead', 'Status', 'Pull Request',
] as const;

/** The one favourites seat (ui_state kind 'branch_fav', key pinned 'code' —
 * one code repo per console; migration 064). */
export const BRANCH_FAV_PATH = '/api/ui-state/branch-favourites/code';

// ---------------------------------------------------------------------
// Pure decision surfaces (drilled by branch-browse.test.mjs)
// ---------------------------------------------------------------------

export interface BranchCellVm {
  col: (typeof BRANCH_COLUMNS)[number];
  /** The rendered cell text — NEVER empty (the never-blank clause). */
  text: string;
  /** Hover title; for an unavailable column this is the server's reason
   * sentence VERBATIM. */
  title: string;
  unavailable: boolean;
}

/** Local-time display for an ISO stamp; the ISO stays VERBATIM in the title
 * (the #375 precedent: local text, exact stamp on hover). */
export function formatAuthoredDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

/** The R52 display as DATA: one VM per column, values exactly as served,
 * unavailability NAMED per column (text 'not available'/'unavailable' with
 * the server's reason verbatim in the title), never blank, never invented. */
export function branchCellVms(
  row: VcsBranchRow,
  columns: VcsBranchesPayload['columns'],
  base: string | null,
): BranchCellVm[] {
  const gap = (g: VcsColumnGap, fallback: string): string =>
    g.reason ?? `${fallback} (reason unstated by the server)`;
  const abAvailable = columns.aheadBehind.available && row.behind !== null && row.ahead !== null;
  const scalarCell = (
    col: (typeof BRANCH_COLUMNS)[number],
    value: unknown,
    g: VcsColumnGap,
    fallback: string,
  ): BranchCellVm =>
    g.available && value !== null && value !== undefined
      ? { col, text: String(value), title: String(value), unavailable: false }
      : { col, text: 'not available', title: gap(g, fallback), unavailable: true };
  return [
    { col: 'Branch', text: row.name, title: row.name, unavailable: false },
    {
      col: 'Commit',
      text: row.sha.slice(0, 10),
      title: `${row.sha} — ${row.subject}`,
      unavailable: false,
    },
    { col: 'Author', text: row.author, title: row.author, unavailable: false },
    {
      col: 'Authored Date',
      text: formatAuthoredDate(row.authoredDate),
      title: row.authoredDate,
      unavailable: false,
    },
    abAvailable
      ? {
          col: 'Behind | Ahead',
          text: `${row.behind} | ${row.ahead}`,
          title: `behind ${row.behind} · ahead ${row.ahead} vs ${base ?? 'the base branch'}`,
          unavailable: false,
        }
      : {
          col: 'Behind | Ahead',
          text: 'unavailable',
          title: gap(columns.aheadBehind, 'behind/ahead unavailable'),
          unavailable: true,
        },
    scalarCell('Status', row.status, columns.status, 'Status unavailable'),
    scalarCell('Pull Request', row.pullRequest, columns.pullRequest, 'Pull Request unavailable'),
  ];
}

export interface BranchTreeNode {
  /** Full path: for a leaf, the branch NAME verbatim; for a folder, the
   * segment prefix ('elite', 'elite/deep'). */
  path: string;
  /** The display segment (last path component). */
  seg: string;
  /** Non-null on a leaf. */
  row: VcsBranchRow | null;
  children: BranchTreeNode[];
  /** Leaves under this folder (folder rows show the count). */
  leafCount: number;
}

/** The ADO tree: '/'-segmented branch names, folders first then branches,
 * each alphabetical. A branch named exactly like a folder prefix keeps its
 * own leaf (git forbids that collision anyway — refs/heads/x vs x/y). */
export function buildBranchTree(rows: VcsBranchRow[]): BranchTreeNode[] {
  const roots: BranchTreeNode[] = [];
  const folders = new Map<string, BranchTreeNode>();
  const folderFor = (segs: string[]): BranchTreeNode | null => {
    if (segs.length === 0) return null;
    const path = segs.join('/');
    const got = folders.get(path);
    if (got) return got;
    const node: BranchTreeNode = {
      path, seg: segs[segs.length - 1] ?? path, row: null, children: [], leafCount: 0,
    };
    folders.set(path, node);
    const parent = folderFor(segs.slice(0, -1));
    (parent ? parent.children : roots).push(node);
    return node;
  };
  for (const row of rows) {
    const segs = row.name.split('/');
    const parent = folderFor(segs.slice(0, -1));
    const leaf: BranchTreeNode = {
      path: row.name, seg: segs[segs.length - 1] ?? row.name, row, children: [], leafCount: 0,
    };
    (parent ? parent.children : roots).push(leaf);
    for (let i = 1; i < segs.length; i++) {
      const f = folders.get(segs.slice(0, i).join('/'));
      if (f) f.leafCount++;
    }
  }
  const sortLevel = (nodes: BranchTreeNode[]): void => {
    nodes.sort((a, b) => {
      const af = a.row === null ? 0 : 1;
      const bf = b.row === null ? 0 : 1;
      return af - bf || a.seg.localeCompare(b.seg);
    });
    for (const n of nodes) sortLevel(n.children);
  };
  sortLevel(roots);
  return roots;
}

export interface VisibleTreeRow { node: BranchTreeNode; depth: number }

/** Flatten for render. `expanded` is the opted-open Set (folders start
 * closed); a non-empty filter matches on the FULL branch name (caseless)
 * and forces matching folders open — corpus-shell's `filter ? true : …`
 * idiom. */
export function flattenTree(
  nodes: BranchTreeNode[],
  expanded: Set<string>,
  filter: string,
): VisibleTreeRow[] {
  const want = filter.trim().toLowerCase();
  const out: VisibleTreeRow[] = [];
  const matches = (n: BranchTreeNode): boolean =>
    n.row !== null
      ? n.row.name.toLowerCase().includes(want)
      : n.children.some(matches);
  const walk = (level: BranchTreeNode[], depth: number): void => {
    for (const n of level) {
      if (want && !matches(n)) continue;
      out.push({ node: n, depth });
      if (n.row === null && (want ? true : expanded.has(n.path))) walk(n.children, depth + 1);
    }
  };
  walk(nodes, 0);
  return out;
}

/** The structure signature (corpus-shell pattern): a change of expansion,
 * stars, filter or the branch population rebuilds the tree; anything else
 * re-renders nothing. */
export function treeSignature(
  names: string[],
  expanded: Set<string>,
  starred: Set<string>,
  filter: string,
  selected: string | null,
): string {
  return JSON.stringify([names, [...expanded].sort(), [...starred].sort(), filter, selected]);
}

/** The measured header line. It LEADS with the WHOLE fetched population
 * (item 14: the client pages until exhausted), because the server's page-1
 * `measured` reads "listed 1000 of 1163 branches …" — the SLICE it served
 * on page one, not the fetch — which the owner correctly read as "only 1000
 * listed". So the header states the full count first and keeps the measured
 * TIMING (total wall-clock across the paged fetch) rather than the stale
 * page-1 slice count; the truncation warning and any serve-stale sentence
 * (verbatim) still ride the tail. */
export function browseHeaderLine(p: VcsBranchesPayload, pages = 1, ms?: number): string {
  const n = p.branches.length;
  const timing = `${pages} page(s)${ms === undefined ? '' : `, ${ms}ms`}`;
  let line = p.truncated
    ? `${n} of ${p.matched} branches fetched in ${timing} — TRUNCATED: the pager stopped early`
    : `${n} branches (fetched in ${timing})`;
  if (p.freshness.stale) line += ` — ${p.freshness.stale}`;
  return line;
}

/** Page ceiling for the fetch-until-exhausted loop: 200 pages × 1000 =
 * 200k branches — an absurdity guard, not a product limit; tripping it
 * renders NAMED in the header line rather than silently. */
export const BRANCH_FETCH_MAX_PAGES = 200;

/** One page size for the loop (the server's own default cap). */
export const BRANCH_FETCH_PAGE = 1000;

/** The result of the fetch-until-exhausted loop. `ms` is the TOTAL
 * wall-clock across every page — the honest cost of the whole listing, and
 * what browseHeaderLine reports (item 14). */
export type BranchFetchResult =
  | { ok: true; payload: VcsBranchesPayload; pages: number; ms: number }
  | { ok: false; status: number; detail: string };

/** Item 14 (D8): fetch EVERY branch — page the server's in-memory slice
 * (?limit&offset) until nextOffset is null; the old client sent no params at
 * all, which is exactly why 1000 was the observed ceiling. Exported so the
 * create modal can PREFETCH the whole listing the instant it opens (before
 * the user clicks Browse), and hand the cache to openBranchBrowser so the
 * modal renders instantly instead of fetching on click. */
export async function fetchAllBranches(): Promise<BranchFetchResult> {
  const t0 = Date.now();
  let acc: VcsBranchesPayload | null = null;
  let offset = 0;
  let pages = 0;
  while (pages < BRANCH_FETCH_MAX_PAGES) {
    const r = await jsonFetch<VcsBranchesPayload & { error?: string }>(
      'GET',
      `/api/vcs/branches?limit=${BRANCH_FETCH_PAGE}${offset ? `&offset=${offset}` : ''}`);
    if (r.status !== 200 || !r.data || !Array.isArray(r.data.branches)) {
      return { ok: false, status: r.status, detail: r.data?.error ?? 'no response' };
    }
    pages++;
    if (!acc) acc = r.data;
    else acc.branches.push(...r.data.branches);
    if (!r.data.truncated) {
      acc.truncated = false; // the loop ran to exhaustion
      return { ok: true, payload: acc, pages, ms: Date.now() - t0 };
    }
    const next = r.data.nextOffset;
    if (next === null || next === undefined) {
      // The server said MORE EXISTS but gave no cursor (pre-offset server) —
      // stop, and the header names the truncation.
      acc.truncated = true;
      return { ok: true, payload: acc, pages, ms: Date.now() - t0 };
    }
    offset = next;
  }
  // Absurdity guard tripped — render what we hold, NAMED as truncated.
  return { ok: true, payload: acc as VcsBranchesPayload, pages, ms: Date.now() - t0 };
}

export interface BranchRowMenuHooks {
  onCopyName: () => void;
  onCopySha: () => void;
  onToggleStar: () => void;
  onTimeline: () => void;
  onUse?: (() => void) | undefined;
}

/** The overflow (…) menu as DATA — rendered by ctxmenu.ts, the existing
 * singleton (R52a forbids a third menu engine). */
export function buildBranchRowMenu(
  row: VcsBranchRow,
  isStarred: boolean,
  hooks: BranchRowMenuHooks,
): CtxMenuItem[] {
  const items: CtxMenuItem[] = [
    { label: isStarred ? 'Unstar branch' : 'Star branch', icon: 'star', run: hooks.onToggleStar },
    { label: 'Browse SHA timeline', icon: 'history', run: hooks.onTimeline },
    { label: '', icon: '', sep: true },
    { label: 'Copy branch name', icon: 'copy', run: hooks.onCopyName },
    { label: `Copy commit sha (${row.sha.slice(0, 10)})`, icon: 'copy', run: hooks.onCopySha },
  ];
  if (hooks.onUse) {
    items.unshift({ label: 'Use this branch', icon: 'git-branch', run: hooks.onUse });
  }
  return items;
}

// ---------------------------------------------------------------------
// The modal
// ---------------------------------------------------------------------

async function jsonFetch<T>(method: string, url: string, body?: unknown): Promise<{ status: number; data: T | null }> {
  try {
    const res = await fetch(url, {
      method,
      headers: body !== undefined ? { 'content-type': 'application/json' } : undefined,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    let data: T | null = null;
    try { data = (await res.json()) as T; } catch { /* tolerate non-JSON */ }
    return { status: res.status, data };
  } catch {
    return { status: 0, data: null };
  }
}

export interface BranchBrowserOptions {
  /** Hand back the picked branch row (Use branch / menu Use). */
  onPick?: ((row: VcsBranchRow) => void) | undefined;
  /** Hand back a sha picked from the chained SHA timeline, with the corpus
   * answer the timeline resolved for it (null when its walk had not landed
   * at pick time — the receiver may ask /api/vcs/corpus-base itself). */
  onPickSha?: ((branch: string, sha: string, answer: CorpusAnswer | null) => void) | undefined;
  /** Override the chained SHA-timeline open (Browse SHAs / the row menu).
   * The create-modal wiring provides this so IT owns the whole browse
   * flow's lifecycle; unset, the browser content-swaps the timeline onto
   * its own host. */
  openTimeline?: ((branch: string) => void) | undefined;
  /** Fired when this dialog's content leaves the host — a real close OR a
   * content swap (engine semantics; the wiring's intent machine needs
   * exactly that). */
  onClose?: (() => void) | undefined;
  /** A prefetched full listing (the create modal fetches it on open, before
   * Browse is clicked) — the modal renders from it INSTANTLY instead of
   * fetching on click. A null / failed / still-in-flight prefetch falls
   * back to the on-click fetchAllBranches; no double-render. */
  preloaded?: BranchFetchResult | null;
}

export interface BranchBrowserHandle extends DialogHandle {
  /** Settles when the initial listing + favourites round trips are done
   * (loaded OR refused — the refusal renders either way). */
  ready: Promise<void>;
}

export function openBranchBrowser(host: HTMLElement, opts: BranchBrowserOptions = {}): BranchBrowserHandle {
  let readyResolve: () => void = () => {};
  const ready = new Promise<void>((res) => { readyResolve = res; });

  const handle = openDialog(host, (body, close) => {
    const title = document.createElement('h2');
    title.className = 'hc-dialog-title';
    title.textContent = 'Browse branches';
    body.appendChild(title);

    const status = document.createElement('p');
    status.className = 'hc-status hc-bb-status';
    status.textContent = 'Loading branches…';
    body.appendChild(status);

    const wrap = document.createElement('div');
    wrap.className = 'hc-bb-wrap';
    wrap.hidden = true;
    body.appendChild(wrap);

    const filterInput = document.createElement('input');
    filterInput.className = 'hc-bb-filter';
    filterInput.type = 'text';
    filterInput.setAttribute('placeholder', 'Filter branches (matches the full name)…');
    filterInput.setAttribute('aria-label', 'Filter branches');
    wrap.appendChild(filterInput);

    const head = document.createElement('div');
    head.className = 'hc-bb-head';
    wrap.appendChild(head);

    const tree = document.createElement('div');
    tree.className = 'hc-bb-tree';
    tree.setAttribute('role', 'listbox');
    tree.setAttribute('aria-label', 'Branches');
    wrap.appendChild(tree);

    const msg = document.createElement('p');
    msg.className = 'hc-dialog-msg';
    body.appendChild(msg);

    // Item 15: the Browse SHAs button is GONE from this modal (the row
    // menu keeps the timeline); Cancel and Use Branch are icon buttons —
    // glyph in, the button NAME on hover (data-tip = aria-label, never
    // title; grid-toolbar's idiom).
    const actions = document.createElement('div');
    actions.className = 'hc-dialog-actions';
    const cancelBtn = mkIconBtn({ glyph: 'x', label: 'Cancel', attrs: { 'data-act': 'no' } });
    cancelBtn.addEventListener('click', close);
    actions.appendChild(cancelBtn);
    let useBtn: HTMLButtonElement | null = null;
    if (opts.onPick) {
      // THE additive action of this modal — nothing else in it creates or
      // commits, so it owns the 'branch-browser' view context outright. It
      // starts DISABLED (no row selected yet); select() promotes it.
      useBtn = mkIconBtn({
        glyph: 'circle-check',
        label: 'Use Branch',
        intent: 'additive',
        ctx: 'branch-browser',
        state: 'disabled',
        cls: 'hc-bb-use',
        attrs: { 'data-act': 'yes' },
      });
      actions.appendChild(useBtn);
    }
    body.appendChild(actions);

    // ----- state (the corpus-shell pattern: Sets + signature guard) -----
    const expanded = new Set<string>();
    const starred = new Set<string>();
    let favRev: number | null = null;
    let favWritable = false;
    let payload: VcsBranchesPayload | null = null;
    let roots: BranchTreeNode[] = [];
    let filter = '';
    let selected: string | null = null;
    let selectedRow: VcsBranchRow | null = null;
    let lastSignature = '';
    let prevVisible = new Set<string>();

    const openTimeline = (branch: string): void => {
      if (opts.openTimeline) {
        opts.openTimeline(branch);
        return;
      }
      // Content-swap on the same host: the engine keeps the overlay up.
      openShaTimeline(host, {
        branch,
        ...(opts.onPickSha
          ? { onPick: (sha: string, answer: CorpusAnswer | null) => opts.onPickSha?.(branch, sha, answer) }
          : {}),
      });
    };

    const persistStars = async (row: VcsBranchRow, nowStarred: boolean): Promise<void> => {
      if (!favWritable) {
        showDialogMsg(msg,
          'favourites seat unavailable — the star will not survive a reload (see the load warning above)');
        return;
      }
      const { status: st, data } = await jsonFetch<{ rev?: number; error?: string }>(
        'PUT', BRANCH_FAV_PATH, { state: { starred: [...starred].sort() } },
      );
      if (st === 200) {
        favRev = data?.rev ?? favRev;
        return;
      }
      // Revert the toggle — a favourite that silently fails to persist is
      // the mechanism-reports-success trap.
      if (nowStarred) starred.delete(row.name); else starred.add(row.name);
      render();
      showDialogMsg(msg, `favourite not saved (${st}): ${data?.error ?? 'no response'}`);
    };

    const toggleStar = (row: VcsBranchRow, starEl: HTMLElement | null): void => {
      const nowStarred = !starred.has(row.name);
      if (nowStarred) starred.add(row.name); else starred.delete(row.name);
      if (starEl && starEl.classList) {
        starEl.classList.remove('hc-bb-star-just');
        starEl.classList.add('hc-bb-star-just');
      }
      render();
      void persistStars(row, nowStarred);
    };

    const select = (row: VcsBranchRow): void => {
      selected = row.name;
      selectedRow = row;
      if (useBtn) setBtnState(useBtn, 'enabled');
      render();
    };

    if (useBtn) {
      useBtn.addEventListener('click', () => {
        if (!selectedRow) return;
        close();
        opts.onPick?.(selectedRow);
      });
    }
    filterInput.addEventListener('input', () => {
      filter = filterInput.value ?? '';
      render();
    });

    const renderHead = (columns: VcsBranchesPayload['columns']): void => {
      head.replaceChildren();
      const starSpacer = document.createElement('span');
      starSpacer.className = 'hc-bb-h hc-bb-h-star';
      head.appendChild(starSpacer);
      for (const col of BRANCH_COLUMNS) {
        const h = document.createElement('span');
        h.className = 'hc-bb-h';
        h.textContent = col;
        // The header carries the per-column unavailability sentence VERBATIM
        // (the R52 named-gap contract) where the server declared one.
        if (col === 'Status' && !columns.status.available) h.dataset.tip = columns.status.reason ?? '';
        if (col === 'Pull Request' && !columns.pullRequest.available) h.dataset.tip = columns.pullRequest.reason ?? '';
        if (col === 'Behind | Ahead' && !columns.aheadBehind.available) h.dataset.tip = columns.aheadBehind.reason ?? '';
        head.appendChild(h);
      }
      const moreSpacer = document.createElement('span');
      moreSpacer.className = 'hc-bb-h hc-bb-h-more';
      head.appendChild(moreSpacer);
    };

    const render = (): void => {
      if (!payload) return;
      const names = payload.branches.map((b) => b.name);
      const sig = treeSignature(names, expanded, starred, filter, selected);
      if (sig === lastSignature) return;
      lastSignature = sig;
      const visible = flattenTree(roots, expanded, filter);
      const nowVisible = new Set(visible.map((v) => v.node.path));
      tree.replaceChildren();
      for (const { node, depth } of visible) {
        tree.appendChild(renderRow(node, depth, !prevVisible.has(node.path)));
      }
      if (visible.length === 0) {
        const none = document.createElement('p');
        none.className = 'hc-bb-none';
        none.textContent = filter
          ? `no branch name contains '${filter.trim()}' (${names.length} listed)`
          : `0 branches listed — ${payload.measured}`;
        tree.appendChild(none);
      }
      prevVisible = nowVisible;
    };

    const renderRow = (node: BranchTreeNode, depth: number, isNew: boolean): HTMLElement => {
      const rowEl = document.createElement('div');
      rowEl.className = node.row === null ? 'hc-bb-row hc-bb-folder' : 'hc-bb-row';
      if (isNew) rowEl.classList.add('hc-bb-row-in');
      rowEl.setAttribute('data-path', node.path);

      if (node.row === null) {
        if (!expanded.has(node.path) && !filter.trim()) rowEl.classList.add('hc-bb-closed');
        const spacer = document.createElement('span');
        spacer.className = 'hc-bb-c hc-bb-c-star';
        rowEl.appendChild(spacer);
        const nameCell = document.createElement('span');
        nameCell.className = 'hc-bb-c hc-bb-c-name';
        nameCell.style.paddingLeft = `${depth * 18}px`;
        // The Bridge's own tree twisty joins the primitive: `chevron-down`
        // rotated -90deg when CLOSED was a third resting convention (the Helm
        // rotates 0->90 from closed; the panes rotate -90->0). One glyph
        // (`chev`), one direction, one token, on the aria-expanded this row
        // already carried.
        const chev = document.createElement('span');
        chev.className = 'hc-bb-chev';
        chev.innerHTML = icon('chev', 'icon disc-chev');
        nameCell.appendChild(chev);
        const label = document.createElement('span');
        label.className = 'hc-bb-name';
        label.textContent = node.seg;
        nameCell.appendChild(label);
        const count = document.createElement('span');
        count.className = 'hc-bb-count';
        count.textContent = `(${node.leafCount})`;
        count.dataset.tip = `${node.leafCount} branch(es) under ${node.path}/`;
        nameCell.appendChild(count);
        rowEl.appendChild(nameCell);
        rowEl.setAttribute('role', 'button');
        rowEl.setAttribute('aria-expanded', String(filter.trim() ? true : expanded.has(node.path)));
        rowEl.addEventListener('click', () => {
          if (expanded.has(node.path)) expanded.delete(node.path);
          else expanded.add(node.path);
          render();
        });
        return rowEl;
      }

      const row = node.row;
      rowEl.setAttribute('role', 'option');
      rowEl.setAttribute('aria-selected', String(selected === row.name));
      if (selected === row.name) rowEl.classList.add('hc-bb-sel');

      // The star WRITES the 064 favourites seat, so it is an action button.
      // Converting it also closes a real defect: it used to carry a native
      // title AND an aria-label that disagreed with it — the title flipped
      // to "Unstar …" when starred, the aria-label always said "Star …", so
      // a screen reader was told the wrong verb on every favourite. One
      // `label` is now both, and cannot drift again.
      const starBtn = mkIconBtn({
        glyph: 'star',
        label: starred.has(row.name)
          ? `Unstar ${row.name}` : `Star ${row.name} (favourite/track)`,
        size: 'sm',
        cls: starred.has(row.name)
          ? 'hc-bb-c hc-bb-star hc-bb-on' : 'hc-bb-c hc-bb-star',
        attrs: { 'aria-pressed': String(starred.has(row.name)) },
      });
      starBtn.addEventListener('click', (ev: Event) => {
        (ev as { stopPropagation?: () => void }).stopPropagation?.();
        toggleStar(row, starBtn);
      });
      rowEl.appendChild(starBtn);

      const cells = branchCellVms(row, payload!.columns, payload!.base);
      let first = true;
      for (const cell of cells) {
        const c = document.createElement('span');
        c.className = `hc-bb-c hc-bb-c-${cell.col.toLowerCase().replace(/[^a-z]+/g, '-')}`;
        if (cell.unavailable) c.classList.add('hc-bb-unavail');
        if (first) {
          c.style.paddingLeft = `${depth * 18 + 20}px`;
          c.textContent = node.seg;
          first = false;
        } else {
          c.textContent = cell.text;
        }
        c.dataset.tip = cell.title;
        rowEl.appendChild(c);
      }

      // The overflow control OPENS ctxmenu.ts's singleton — a popover
      // TRIGGER, which PR-3b owns; stamped for buttons-check, not converted.
      // The name moves off native title= onto the shared tooltip layer.
      const moreBtn = document.createElement('button');
      moreBtn.dataset.btnRole = 'trigger';
      moreBtn.type = 'button';
      moreBtn.className = 'hc-bb-c hc-bb-more';
      moreBtn.innerHTML = icon('ellipsis');
      moreBtn.setAttribute('data-tip', `More actions for ${row.name}`);
      moreBtn.setAttribute('aria-label', `More actions for ${row.name}`);
      moreBtn.addEventListener('click', (ev: Event) => {
        const e = ev as MouseEvent & { stopPropagation?: () => void };
        e.stopPropagation?.();
        const items = buildBranchRowMenu(row, starred.has(row.name), {
          onCopyName: () => { void copyPlainText(row.name); },
          onCopySha: () => { void copyPlainText(row.sha); },
          onToggleStar: () => toggleStar(row, null),
          onTimeline: () => openTimeline(row.name),
          onUse: opts.onPick ? () => { close(); opts.onPick?.(row); } : undefined,
        });
        openCtxMenu(e.clientX ?? 0, e.clientY ?? 0, items);
      });
      rowEl.appendChild(moreBtn);

      rowEl.addEventListener('click', () => select(row));
      return rowEl;
    };

    // Item 14 (D8): the whole listing comes from fetchAllBranches (module
    // scope now, so the create modal can PREFETCH it on open). If the caller
    // handed us a ready prefetch (opts.preloaded), render from it INSTANTLY —
    // no on-click fetch; a null / failed / still-in-flight prefetch falls
    // back to the on-click fetch (no double-render).
    const preloaded = opts.preloaded ?? null;
    void (async () => {
      const [branchesRes, favRes] = await Promise.all([
        preloaded && preloaded.ok ? Promise.resolve(preloaded) : fetchAllBranches(),
        jsonFetch<{ state?: { starred?: unknown } | null; rev?: number; error?: string }>(
          'GET', BRANCH_FAV_PATH),
      ]);
      if (!branchesRes.ok) {
        // VERBATIM refusal — a 502 mirror failure must never read as an
        // empty tree (openForkPicker's doctrine, ported).
        status.textContent =
          `Branch listing unavailable (${branchesRes.status}): ${branchesRes.detail}`;
        readyResolve();
        return;
      }
      payload = branchesRes.payload;
      roots = buildBranchTree(payload.branches);
      if (favRes.status === 200) {
        favWritable = true;
        favRev = favRes.data?.rev ?? null;
        const list = favRes.data?.state?.starred;
        if (Array.isArray(list)) {
          for (const n of list) if (typeof n === 'string') starred.add(n);
        }
      } else {
        showDialogMsg(msg,
          `favourites unavailable (${favRes.status}): ${favRes.data?.error ?? 'no response'} — stars will not persist`);
      }
      status.textContent = browseHeaderLine(payload, branchesRes.pages, branchesRes.ms);
      renderHead(payload.columns);
      wrap.hidden = false;
      render();
      // Item 13: the app's custom scrollbar (pi-stack scroll rails) on the
      // tree — browser-only; the stub harness has no rail substrate.
      try {
        if (typeof requestAnimationFrame === 'function'
          && (tree as { ownerDocument?: unknown }).ownerDocument) {
          attachElementScrollRail(tree, { host: wrap });
        }
      } catch { /* rail is a nicety; the tree still scrolls natively */ }
      readyResolve();
    })();
  }, { className: 'hc-bb-dialog', ...(opts.onClose ? { onClose: opts.onClose } : {}) });

  return { ...handle, ready };
}

// ---------------------------------------------------------------------
// The create-modal wiring (R57's second half + the R51/R56 entry points).
//
// STACKED since the owner batch 2026-09-05 (item 9, D7): the browse
// modals open on the stacked overlay host ABOVE the create form, which
// STAYS MOUNTED — a pick writes the form's inputs directly and dispatches
// 'input' so the validation icons re-judge. The old stash/swap/reopen
// intent machine (built because two open hosts double-fired the engine's
// document-level Escape/Tab handlers) is retired: the engine's modal
// stack now makes the TOPMOST host the only key handler. Chained browse
// (branch browser -> SHA timeline via the row menu) content-swaps on the
// stacked host, so stacking depth is bounded at 2.
// ---------------------------------------------------------------------

export interface CreateBrowseSeats {
  /** The element the create form lives in (the dialog panel or the host —
   * anything the two mount ids resolve under). */
  panel: HTMLElement;
  /** The BASE modal host (the create form's); browse modals open on
   * stackHostFor(host), above it. */
  host: HTMLElement;
  /** Read at Browse-click time: the create modal's prefetched full listing
   * (fetched on modal open), so the branch browser renders from cache
   * instantly. Returns null while the prefetch is in flight or after it
   * failed — the browser then falls back to its own on-click fetch. */
  getPreloaded?: (() => BranchFetchResult | null) | undefined;
}

/** R57 into the create surface: one line under the sha field carrying the
 * walk's measured sentence VERBATIM (or the refusal by name). Exposed for
 * the suite; showDialogMsg gives the answer its registered entrance. */
export async function renderCorpusLine(
  line: HTMLElement,
  q: { branch?: string; sha?: string },
): Promise<void> {
  const param = q.sha
    ? `sha=${encodeURIComponent(q.sha)}`
    : `branch=${encodeURIComponent(q.branch ?? '')}`;
  const { status, data } = await jsonFetch<{ measured?: string; error?: string }>(
    'GET', `/api/vcs/corpus-base?${param}`);
  showDialogMsg(line, status === 200 && data?.measured
    ? data.measured
    : `corpus answer unavailable (${status}): ${data?.error ?? 'no response'}`);
}

/** Decorate the create form's named mount seats. Call after EVERY
 * create-dialog build (create-dialog.ts's openCreateWired does).
 *
 * Owner batch (seam:create-modal-fields): the SHA field now mirrors the
 * branch field — its Browse-SHAs icon button sits INLINE to the right of the
 * field (#hcShaBrowseMount, built by create-dialog.ts), and the R57 corpus
 * line moved to its OWN line below (#hcShaTimelineMount). */
export function mountCreateBrowse(seats: CreateBrowseSeats): void {
  const { panel, host } = seats;
  const nameInput = panel.querySelector('input[name="name"]') as HTMLInputElement | null;
  const branchInput = panel.querySelector('input[name="source_branch"]') as HTMLInputElement | null;
  const shaInput = panel.querySelector('input[name="source_sha"]') as HTMLInputElement | null;
  const browseMount = panel.querySelector('#hcBranchBrowseMount') as HTMLElement | null;
  const shaBrowseMount = panel.querySelector('#hcShaBrowseMount') as HTMLElement | null;
  const shaMount = panel.querySelector('#hcShaTimelineMount') as HTMLElement | null;
  if (!nameInput || !branchInput || !shaInput || !browseMount || !shaBrowseMount || !shaMount) {
    return; // seats absent — nothing to wire
  }
  void nameInput; // the form stays mounted now — no stash needs its value

  // --- the two affordances (item 5: icon buttons, the button NAME on
  // hover — data-tip + aria-label, never title=; on the primitive since
  // Styles × Themes PR-2). Both OPEN a picker, which is an action: they are
  // neutral, and the create dialog's one additive seat stays its Create
  // button's. ---
  const browseBtn = mkIconBtn({ glyph: 'git-branch', label: 'Browse Branches' });
  browseMount.replaceChildren(browseBtn);

  // The Browse-SHAs button rides INLINE to the right of the SHA field (the
  // field row is #hcShaBrowseMount's parent, built by create-dialog.ts).
  // hc-create-shas is kept as the seat's name hook (branch-browse.test.mjs
  // addresses it); the label and the enabled/disabled face are the
  // primitive's, written by syncShasBtn below.
  const shasBtn = mkIconBtn({
    glyph: 'history', label: 'Browse SHAs', cls: 'hc-create-shas',
  });
  shaBrowseMount.replaceChildren(shasBtn);

  // The R57 corpus-answer line lives on its OWN line below the SHA field row.
  const corpusLine = document.createElement('p');
  corpusLine.className = 'hc-create-corpus';
  shaMount.replaceChildren(corpusLine);

  const syncShasBtn = (): void => {
    const b = branchInput.value.trim();
    setBtnState(shasBtn, b === '' ? 'disabled' : 'enabled');
    setBtnLabel(shasBtn, b === ''
      ? 'Browse SHAs — type or browse a branch first (the timeline walks one branch)'
      : 'Browse SHAs');
  };
  branchInput.addEventListener('input', syncShasBtn);

  syncShasBtn();

  // --- the browse flows (stacked host, direct writes — D7) --------------
  // The form never unmounts: a pick writes the inputs and dispatches
  // 'input' so create-dialog's validation icons re-judge; Cancel/Escape on
  // the stacked host simply closes it. No stash, no reopen, no intent
  // machine.
  const setFields = (fields: { branch?: string; sha?: string }): void => {
    if (fields.branch !== undefined) branchInput.value = fields.branch;
    if (fields.sha !== undefined) shaInput.value = fields.sha;
    if (typeof Event === 'function' && typeof branchInput.dispatchEvent === 'function') {
      if (fields.branch !== undefined) branchInput.dispatchEvent(new Event('input'));
      if (fields.sha !== undefined) shaInput.dispatchEvent?.(new Event('input'));
    }
    syncShasBtn();
  };

  const applyShaPick = (branch: string, sha: string, answer: CorpusAnswer | null): void => {
    setFields({ branch, sha });
    if (answer?.measured) showDialogMsg(corpusLine, answer.measured); // verbatim
    else void renderCorpusLine(corpusLine, { sha });
  };

  const openTimelineOnStack = (branch: string): void => {
    openShaTimeline(stackHostFor(host), {
      branch,
      onPick: (sha, answer) => applyShaPick(branch, sha, answer),
    });
  };

  browseBtn.addEventListener('click', () => {
    openBranchBrowser(stackHostFor(host), {
      // Render from the modal-open prefetch if it has landed (read at click
      // time, so a prefetch that resolves AFTER mount still counts); null
      // falls back to the on-click fetch inside openBranchBrowser.
      preloaded: seats.getPreloaded?.() ?? null,
      onPick: (row) => {
        // A new cut base invalidates a sha typed for the old one: clear it
        // and answer R57 for the BRANCH (its tip walk).
        setFields({ branch: row.name, sha: '' });
        void renderCorpusLine(corpusLine, { branch: row.name });
      },
      onPickSha: (branch, sha, answer) => applyShaPick(branch, sha, answer),
      // The row menu's timeline content-swaps on the SAME stacked host —
      // stacking depth stays bounded at 2.
      openTimeline: (branch) => openTimelineOnStack(branch),
    });
  });

  shasBtn.addEventListener('click', () => {
    const b = branchInput.value.trim();
    if (b === '') return;
    openTimelineOnStack(b);
  });
}
